import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, first, map } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { BackblazeBucketFileList, IEventsMapTiles } from '../../models/index';

export interface AppSettings {
  alertMessage: string;
  debugApi: boolean;
  disabledEmailCheck: boolean;
  disabledEmailVerification: boolean;
  fingerprintActive: boolean;
  inetLogging: boolean;
  minimalTotalRequest: number;
  maximumChallengeTrialsByFp: number;
  maximumValidateTrialsBeforeChallenge: number;
  maxLoginAttempts: number;
  maxPayinFailures: number;
  payoutDelayinHours: number;
  enableAutoPurchase: boolean;
  eventsMap: boolean;
  eventsMapTiles: IEventsMapTiles;
  slowDeliveryDomains: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AdminSettingsService {
  private http = inject(HttpClient);

  configUrl = environment.apiUrl;

  getBackupList() {
    const route = `${this.configUrl}w/a/listBackups`;
    return this.http.get<BackblazeBucketFileList>(route).pipe(
    );
  }

  startBackup() {
    const route = `${this.configUrl}w/a/startBackup`;
    return this.http.get<any>(route).pipe(
    );
  }

  downloadBackup(id: string) {
    const route = `${this.configUrl}w/a/doownloadBackup/${id}`;
    return this.http.get(route).pipe(
    );
  }

  updateAppSettings(settings: AppSettings) {
    const route = `${this.configUrl}settings/appSettings`;

    return this.http.put<AppSettings>(route, settings).pipe(
      catchError((error) => this.handleSettingsError(error)),
      first(),
    );
  }

  getAppSettings() {
    const route = `${this.configUrl}settings/appSettings`;
    return this.http.get<AppSettings>(route).pipe(
      catchError((error) => this.handleSettingsError(error)),
      first(),
    );
  }

  getMangopayRateLimit() {
    const route = `${this.configUrl}w/a/mangopay/rateLimits`;
    return this.http.get<{rateLimits: Array<{
      minutesInterval: number;
      callsMade: number;
      callsRemaining: number;
      resetTimeMillis: number;
    }>}>(route).pipe(
      first(),
    );
  }

  getRateLimitEntries() {
    const route = `${this.configUrl}r/a/rateLimitEntries`;
    return this.http.get<Array<{
      key: string;
      points: number;
      expire: string;
    }>>(route).pipe(
      map( (rateLimits) => rateLimits.map( (l) => ({
        key: l.key,
        points: l.points,
        expire: new Date(parseInt(l.expire, 10)),
      })),
      ),
      first(),
    );
  }

  deleteRateLimitEntry(key: string) {
    const route = `${this.configUrl}r/a/rateLimitEntry?entryKey=${encodeURIComponent(key)}`;
    return this.http.delete<boolean>(route)
      .pipe(
        first(),
      );
  }

  private handleSettingsError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // todo clean
      // A client-side or network error occurred. Handle it accordingly.
      // eslint-disable-next-line no-console
      console.error('An error occurred:', error.error.message);
    } else if (error.error && (error.error.name === 'MangoError' || error.error.name === 'ReelaxError')) {
      // eslint-disable-next-line no-console
      console.log('reelax error');
      return throwError(error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      // eslint-disable-next-line no-console
      console.error(
        `Backend returned code ${error.status}, ` +
        'body was:');
      // eslint-disable-next-line no-console
      console.error(error.error);
    }
    // return an observable with a user-facing error message
    return throwError(
      'Something bad happened into settings service; please try again later.');
  }
}
